import React, { useEffect, useState } from 'react';
import { Check, ChevronDown, LockKeyhole } from 'lucide-react';
import { approvalsApi, ApprovalStatus, estimateApi, Pipeline } from './api';
import { fmtDate } from './utils';
import './estimate-approval-gate.css';

interface Snapshot {
  projectId: string;
  phase: 'review' | 'approved';
  targetId: string;
  status: ApprovalStatus;
}

/** The project pipeline selects the CURRENT revision. A previously approved
 * document may still be listed while the current architecture needs a fresh
 * estimate, so never infer the gate from the document list alone. */
export function useEstimateApprovalGate(projectId: string, pipeline: Pipeline | null | undefined, canReadEstimate: boolean) {
  const [snapshot, setSnapshot] = useState<Snapshot | null>(null);
  const phase = pipeline?.estimate;
  useEffect(() => {
    if (!canReadEstimate || !projectId || pipeline?.architecture !== 'approved' || (phase !== 'review' && phase !== 'approved')) {
      setSnapshot(null);
      return undefined;
    }
    let cancelled = false;
    (async () => {
      try {
        const { estimates } = await estimateApi.listEstimates(projectId);
        // Match the server's current-revision ordering, including documents
        // created in the same timestamp tick.
        const latest = [...(estimates || [])].sort((a, b) =>
          Date.parse(b.created_at) - Date.parse(a.created_at) || b.id.localeCompare(a.id))[0];
        if (!latest || latest.status !== phase) {
          if (!cancelled) setSnapshot(null);
          return;
        }
        const status = await approvalsApi.getStatus({ step: 'estimate', target_kind: 'estimate', target_id: latest.id });
        if (!cancelled) setSnapshot({ projectId, phase, targetId: latest.id, status });
      } catch {
        // A generator cannot read Estimate details; a transient read failure
        // still leaves the server-owned pipeline gate and neutral explanation.
        if (!cancelled) setSnapshot(null);
      }
    })();
    return () => { cancelled = true; };
  }, [projectId, phase, pipeline, canReadEstimate]);
  return snapshot?.projectId === projectId && snapshot.phase === phase ? snapshot.status : null;
}

export interface EstimateGateView {
  headline: string;
  votes: Array<{ name: string; when: string }>;
  next: string | null;
  reason: string | null;
  locked: boolean;
  stale: boolean;
}

export function estimateGateView(pipeline: Pipeline | null | undefined, status: ApprovalStatus | null, userId: string | null | undefined): EstimateGateView | null {
  if (!pipeline || pipeline.architecture !== 'approved') return null;
  if (pipeline.estimate === 'none') {
    if (!pipeline.historical?.estimate && !pipeline.running?.estimate) return null;
    const stale = !!pipeline.historical?.estimate;
    return {
      headline: stale ? pipeline.running?.estimate ? 'Preparing new estimate' : 'Current estimate required' : 'Preparing estimate',
      votes: [], next: null, stale, locked: true,
      reason: stale
        ? pipeline.running?.estimate
          ? 'The approved estimate belongs to an earlier architecture. Pricing opens when the new estimate is approved.'
          : 'The earlier estimate is saved in history. The current estimate starts automatically and Pricing opens after its approval.'
        : 'A new estimate is being prepared. Pricing opens after its approval.',
    };
  }
  const pending = pipeline.estimate === 'review';
  const votes = (status?.approvals || []).map((vote, index) => ({
    // The backend intentionally hides other Estimate voters' identities from
    // non-root accounts. Keep the timestamp, but never infer the name.
    name: vote.user_name || (vote.user_id === userId ? 'You' : `Approval ${index + 1}`),
    when: fmtDate(vote.approved_at),
  }));
  const required = status?.required || 1;
  const given = status?.given || 0;
  const headline = pending
    ? required > 1 ? `Estimate review · ${given} of ${required}` : 'Estimate awaiting approval'
    : 'Estimate approved';
  const votedIds = new Set((status?.approvals || []).map(vote => vote.user_id));
  const waiting = (status?.approvers || []).filter(person => !votedIds.has(person.id)).map(person => person.display_name || person.username);
  const next = !pending ? null : status?.turn?.name
    ? `Next: ${status.turn.name}`
    : waiting.length ? `Waiting for ${waiting.join(', ')}` : status ? 'Waiting for an estimate approver' : 'Checking approval order…';
  return {
    headline, votes, next, locked: pending, stale: false,
    reason: pending
      ? status?.turn?.name
        ? `Pricing is locked until ${status.turn.name} approves the estimate${required > 1 ? ` (${given} of ${required} complete)` : ''}.`
        : status
          ? `Pricing is locked until the estimate receives ${required > 1 ? `all ${required} approvals` : 'approval'}.`
          : 'Pricing opens after every required estimate approval. Checking who is next…'
      : null,
  };
}

export default function EstimateApprovalGate({ view, variant }: { view: EstimateGateView | null; variant: 'web' | 'tma' }) {
  if (!view) return null;
  return (
    <details className={`estimate-approval-gate estimate-approval-gate--${variant}${view.stale ? ' is-stale' : ''}`}>
      <summary title={view.reason || view.headline}>
        <span className="estimate-gate-headline">{view.headline}</span>
        {view.votes[0] ? <span className="estimate-gate-vote"><Check size={12} aria-hidden="true" />{view.votes[0].name}{view.votes[0].when ? ` · ${view.votes[0].when}` : ''}</span> : null}
        {view.next ? <span className="estimate-gate-next">{view.next}</span> : null}
        {view.locked ? <span className="estimate-gate-lock"><LockKeyhole size={12} aria-hidden="true" />Pricing locked</span> : null}
        <ChevronDown className="estimate-gate-chevron" size={14} aria-hidden="true" />
      </summary>
      <div className="estimate-gate-detail">
        {view.votes.length ? <ol aria-label="Estimate approvals">{view.votes.map((vote, index) => <li key={index}><Check size={13} aria-hidden="true" /><span>{vote.name}</span>{vote.when ? <time>{vote.when}</time> : null}</li>)}</ol> : null}
        {view.next ? <p className="estimate-gate-turn">{view.next}</p> : null}
        {view.reason ? <p>{view.reason}</p> : null}
      </div>
    </details>
  );
}
